import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import {
  clearAuthSession,
  getAccessToken,
  getStoredPermissions,
  getStoredSessionUser,
  isAccessTokenExpired,
  setStoredSessionUser,
} from "@/lib/auth";
import { fetchProfile } from "@/lib/authApi";
import { resolveEffectivePermissions } from "@/lib/permissions";

const RoleContext = createContext(null);

function departmentFromUser(user) {
  if (!user) return null;
  const name = user.department_name?.trim();
  if (!name && !user.department_id) return null;
  return {
    id: user.department_id ?? null,
    name: name || "",
  };
}

export function RoleProvider({ children }) {
  const [sessionUser, setSessionUserState] = useState(() => getStoredSessionUser());
  const [ready, setReady] = useState(false);

  const hydrateSession = useCallback(async () => {
    if (!getAccessToken()) return;
    if (isAccessTokenExpired()) {
      clearAuthSession();
      setSessionUserState(null);
      return;
    }

    let user = getStoredSessionUser();

    try {
      const profile = await fetchProfile(user?.mobile);
      if (profile) {
        user = { ...(user || {}), ...profile, rbac: getStoredPermissions() };
        setStoredSessionUser(user);
        setSessionUserState(user);
        return;
      }
    } catch {
      /* Profile unavailable — fall back to cached session user. */
    }

    if (user) {
      setSessionUserState(user);
    }
  }, []);

  useEffect(() => {
    (async () => {
      await hydrateSession();
      setReady(true);
    })();
  }, [hydrateSession]);

  const setSessionUser = useCallback((user) => {
    setSessionUserState(user);
    if (user) setStoredSessionUser(user);
  }, []);

  const currentUser = sessionUser;
  const currentDepartment = useMemo(
    () => departmentFromUser(currentUser),
    [currentUser?.department_id, currentUser?.department_name],
  );

  const permissions = useMemo(
    () => resolveEffectivePermissions(sessionUser),
    [sessionUser?.id, sessionUser?.rbac],
  );

  const value = {
    ready,
    currentUser,
    currentDepartment,
    permissions,
    setSessionUser,
  };

  return <RoleContext.Provider value={value}>{children}</RoleContext.Provider>;
}

export function useRole() {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error("useRole must be used within RoleProvider");
  return ctx;
}
